/**
 * What the composer knows: how a reply is built, when a draft is saved, and
 * whether this account can send at all.
 *
 * Everything here is the part of composing that is not a rectangle on screen.
 * It lives outside `Compose.tsx` because the hard parts -- which addresses are
 * *you*, which revision of a draft the next save replaces, and the difference
 * between a send that was accepted and one that was delivered -- are decisions,
 * not layout, and each of them has a wrong answer that ships silently.
 *
 * # Sending is built on `mutate.ts` and nothing else
 *
 * There is exactly one `Email/set` + `EmailSubmission/set` in this client and
 * it is `mutate.sendEmail`. Read its doc comment before changing anything here:
 * the single-request shape, the `#d` creation-id reference and
 * `onSuccessUpdateEmail` are load-bearing, and a second implementation is how
 * one of them silently stops matching the other.
 *
 * # Accepted is not sent
 *
 * `EmailSubmission/set` returning an id means a row is in the daemon's outbox.
 * The bytes leave later, when `hm_sync::drain` reaches it, and `undoStatus` is
 * `pending` until then. So this module still never reports "sent" on a 200. It
 * reports *handed over*, and hands the submission to `lib/outbox.ts`, which
 * owns the rest: the hold, the drain, the verdict and the undo.
 *
 * That is why the composer may now close on a 200 when it previously could not.
 * The rule has not been relaxed -- it has been paid for. The window closes only
 * after the transport's `announceSubmission` has carried the message to the
 * outbox strip -- every window's strip, which matters on the desktop, where
 * this composer *is* a window and its own strip dies with it -- so "accepted,
 * not sent" is still on screen; it is simply on screen somewhere that survives
 * the composer. **The ordering in {@link send} is the whole of that
 * guarantee**: announce, then close. Closing first would leave a message that
 * failed with nothing anywhere to say so, which is the exact failure the old
 * thirty-second wait existed to prevent.
 *
 * # The undo window is chosen here and enforced there
 *
 * `send_hold_secs` on the daemon is what actually holds a message back, and a
 * number in this client that the daemon ignored would be the worst outcome
 * available -- an undo button over a message already gone. So the preference in
 * `lib/settings.ts` is not a client-side timer: it is turned into the `sendAt`
 * on the submission itself, which RFC 8621 defines and this daemon honours
 * ahead of its own default. See {@link releaseFor}. The countdown is then drawn
 * from the `sendAt` the *server* returned, never from what was asked for.
 *
 * # An account that cannot send must say so before the first keystroke
 *
 * `EmailSubmission/set` refuses up front when no submission host is configured,
 * which is the state of every IMAP account that has not been told where to
 * send. Discovering that after writing eight hundred words is the failure this
 * module exists to prevent -- see {@link probeSubmission} for how it is asked,
 * and why it is asked that way. The answer is no longer a CLI command to copy
 * out: Settings -> Accounts configures it, so the banner sends people there.
 */

// The transport seam. `@transport` resolves at build time to the browser
// implementation or the desktop one; nothing below can tell which. Imported
// under the name `fetch` so the call sites are untouched and this import is
// the whole of the difference. See `lib/net.types.ts`.
import { announceSubmission, closeComposeWindow, request as fetch, windowRole } from "@transport";
import { bearer } from "./settings";
import { batch, computed, signal } from "@preact/signals";

import { SUBMISSION, call, serverNow, type Invocation } from "./jmap";
import { MIRROR_KEY } from "./keys";
import { isPublicSession } from "./publicSession";
import {
  SetFailed,
  destroyEmails,
  saveDraft,
  sendEmail,
  type DraftInput,
  type Release,
} from "./mutate";
import { composeFormat, undoSendSeconds } from "./settings";
import { escapeHtml } from "./html";
import * as jmap from "./jmap";
import * as store from "./store";
import type { Email, EmailAddress, Envelope, Mailbox } from "./types";

// ---- addresses ---------------------------------------------------------------

/**
 * The comparison key for "is this the same person".
 *
 * Case-folded, and the `+tag` removed. Both are safe: no mail system treats
 * `A@x.org` and `a@x.org` as different people, and a plus tag is by definition
 * a label on one mailbox.
 *
 * What this deliberately does *not* do is strip dots. That normalisation is
 * correct for Gmail and wrong everywhere else, and the two failures are not
 * symmetric -- treating `j.smith@` and `jsmith@` as one person costs a real
 * recipient their copy of the message on any other provider. The daemon's
 * address book does normalise dots, because it knows the provider; this does
 * not, because it does not.
 */
export function addressKey(email: string): string {
  const at = email.lastIndexOf("@");
  if (at < 0) return email.trim().toLowerCase();
  const local = email.slice(0, at).toLowerCase();
  const domain = email.slice(at + 1).trim().toLowerCase();
  const plus = local.indexOf("+");
  return `${plus < 0 ? local : local.slice(0, plus)}@${domain}`;
}

/**
 * Every address that is the person at this keyboard.
 *
 * Two sources, because neither alone is enough. Account names are addresses on
 * this daemon (`amit@achaudhari.com`), which covers the common case at zero
 * cost; the `From` of the account's own sent mail covers the aliases nobody
 * wrote down -- the address a mailing list rewrites to, the old domain still
 * forwarding, the `+` variants used for signup.
 *
 * This is the input to reply-all, and getting it short is the classic bug:
 * every address missing from this set is one the user copies themselves on.
 */
export const selfAddresses = signal<Set<string>>(new Set());

let identitiesLearned: Promise<void> | null = null;

/** True if `email` is one of the user's own addresses. */
export function isSelf(email: string): boolean {
  return selfAddresses.value.has(addressKey(email));
}

/**
 * Fill {@link selfAddresses}, once per session.
 *
 * Best effort throughout: a failure inside resolves anyway, with whatever was
 * learned before it. Most callers fire-and-forget, but the promise is cached
 * rather than a boolean flag so that the one caller who must wait, can:
 * reply-all reads {@link selfAddresses} at open, and a second call landing
 * mid-learn has to get the learn in flight, not an instant "done".
 */
export function learnIdentities(): Promise<void> {
  identitiesLearned ??= actuallyLearnIdentities();
  return identitiesLearned;
}

async function actuallyLearnIdentities(): Promise<void> {
  const found = new Set<string>();
  const accounts = store.accounts.value;
  for (const account of accounts) {
    // The account's name is its address on this daemon. Guarded rather than
    // assumed: a tenant that named an account "work" must not put the literal
    // string "work" into the set of things that count as the user.
    if (account.name.includes("@")) found.add(addressKey(account.name));
  }
  selfAddresses.value = found;

  // The Sent folders, for the aliases. One request for every account at once.
  const sentBoxes = accounts
    .map((a) => ({
      accountId: a.id,
      mailboxId: store.mailboxes.value.get(a.id)?.find((m) => m.role === "sent")?.id,
    }))
    .filter((s): s is { accountId: string; mailboxId: string } => s.mailboxId !== undefined);
  if (sentBoxes.length === 0) return;

  try {
    const calls: Invocation[] = [];
    sentBoxes.forEach((s, i) => {
      calls.push([
        "Email/query",
        {
          accountId: s.accountId,
          filter: { inMailbox: s.mailboxId },
          sort: [{ property: "receivedAt", isAscending: false }],
          limit: 40,
        },
        `q${i}`,
      ]);
      // The back-reference, so the addresses come back in the same round trip
      // rather than in a second one nobody is waiting for.
      calls.push([
        "Email/get",
        {
          accountId: s.accountId,
          "#ids": { resultOf: `q${i}`, name: "Email/query", path: "/ids" },
          properties: ["id", "from"],
        },
        `g${i}`,
      ]);
    });
    const results = await call(calls);

    const grown = new Set(found);
    for (let i = 0; i < sentBoxes.length; i += 1) {
      const args = results.get(`g${i}`);
      if (!args || args.__error) continue;
      for (const email of (args.list as Email[] | undefined) ?? []) {
        for (const from of email.from ?? []) grown.add(addressKey(from.email));
      }
    }
    selfAddresses.value = grown;
  } catch {
    // The account names alone are still a useful answer.
  }
}

/**
 * Split a pasted address list into the strings {@link parseAddress} expects.
 *
 * Not a regex split: a display name may legally contain the separator --
 * `"Doe, John" <j@x.org>` is one recipient -- so the walk tracks double
 * quotes and angle brackets and cuts only at depth zero. Commas, semicolons
 * and line breaks all separate, because those are what address books and
 * other clients put between addresses on the clipboard. Empty segments
 * (doubled commas, trailing separators) are dropped.
 */
export function splitAddressList(raw: string): string[] {
  const parts: string[] = [];
  let current = "";
  let quoted = false;
  let angled = false;
  for (const ch of raw) {
    if (ch === '"' && !angled) quoted = !quoted;
    else if (ch === "<" && !quoted) angled = true;
    else if (ch === ">" && !quoted) angled = false;
    if (!quoted && !angled && (ch === "," || ch === ";" || ch === "\n" || ch === "\r")) {
      if (current.trim() !== "") parts.push(current.trim());
      current = "";
      continue;
    }
    current += ch;
  }
  if (current.trim() !== "") parts.push(current.trim());
  return parts;
}

/** Parse `Sarah Okonkwo <s@x.org>`, or a bare address, into an address object. */
export function parseAddress(raw: string): EmailAddress | null {
  const text = raw.trim().replace(/[,;]+$/u, "").trim();
  if (text === "") return null;

  const angled = /^(.*?)<([^<>]+)>$/u.exec(text);
  if (angled) {
    const name = angled[1]!.trim().replace(/^"(.*)"$/u, "$1").trim();
    const email = angled[2]!.trim();
    return email.includes("@") ? { name: name || null, email } : null;
  }
  return text.includes("@") && !/\s/u.test(text) ? { name: null, email: text } : null;
}

/** `Sarah <s@x.org>` for a header, or the bare address when there is no name. */
export function formatAddress(a: EmailAddress): string {
  return a.name ? `${a.name} <${a.email}>` : a.email;
}

/** Drop repeats, keeping the first spelling of each -- which carries the name. */
function dedupe(list: EmailAddress[]): EmailAddress[] {
  const seen = new Set<string>();
  const out: EmailAddress[] = [];
  for (const a of list) {
    const key = addressKey(a.email);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}

function withoutSelf(list: EmailAddress[]): EmailAddress[] {
  return list.filter((a) => !isSelf(a.email));
}

// ---- the draft ----------------------------------------------------------------

/**
 * An attachment on an outgoing message.
 *
 * Always a blob id the server already holds. There is no path here for bytes
 * from the local disk, because there is no endpoint to put them through: see
 * {@link uploadLimit}.
 */
export interface ComposeAttachment {
  blobId: string;
  name: string;
  type: string;
  size: number;
  /**
   * Set for a picture the HTML body shows by `cid:`. Such a part is sent
   * `inline` with its `Content-ID`, is not listed as an attachment on screen,
   * and is dropped from the message when the body no longer refers to it.
   */
  cid?: string | null;
}

export type ComposeKind = "new" | "reply" | "replyAll" | "forward" | "draft";

export interface ComposerDraft {
  accountId: string;
  to: EmailAddress[];
  cc: EmailAddress[];
  bcc: EmailAddress[];
  subject: string;
  /**
   * The plain-text body. In a rich message this is the alternative the editor
   * derives from {@link html}, kept here so that everything that reads the
   * body -- the autosave, the mirror, the "mentions an attachment" check --
   * keeps reading one string.
   */
  body: string;
  /**
   * The HTML body, or `null` for a plain-text message. The format is a
   * property of the draft rather than of the window: a draft reopened on
   * another device comes back in the format it was written in.
   */
  html: string | null;
  /**
   * Whether the Cc and Bcc rows are on screen.
   *
   * Part of the draft rather than of the component, so that reopening a draft
   * that *has* a Bcc shows it. A blind copy the user has forgotten about is how
   * the wrong person gets a copy, and hiding a populated row to keep the form
   * tidy is the mechanism.
   */
  showCc: boolean;
  showBcc: boolean;
  inReplyTo: string[] | null;
  references: string[] | null;
  attachments: ComposeAttachment[];
}

const EMPTY: ComposerDraft = {
  accountId: "",
  to: [],
  cc: [],
  bcc: [],
  subject: "",
  body: "",
  html: null,
  showCc: false,
  showBcc: false,
  inReplyTo: null,
  references: null,
  attachments: [],
};

export const draft = signal<ComposerDraft>({ ...EMPTY });
export const kind = signal<ComposeKind>("new");
/**
 * Bumped whenever the composer starts over -- a new message, a reply, a
 * resumed draft, a change of format. The rich editor keys on it: it reads its
 * markup once and must not be reloaded by anything short of a fresh start,
 * least of all by the draft gaining an id on its first save.
 */
export const generation = signal(0);

/**
 * The id of the draft's most recent upstream revision.
 *
 * This is the single most important variable in the autosave, and the one whose
 * mishandling has a name: every save that forgets to pass it as `replacing`
 * leaves the previous revision behind, so a message typed over two minutes ends
 * up as forty drafts. It is set from the id `saveDraft` returns, and adopted
 * from the message id when an existing draft is opened for editing.
 */
export const draftId = signal<string | null>(null);

export type SaveState = "clean" | "dirty" | "saving" | "saved" | "failed";
export const saveState = signal<SaveState>("clean");
export const saveError = signal<string | null>(null);

/**
 * Three states, where there used to be five.
 *
 * `queued` and `sent` are gone from the composer, because the composer no
 * longer outlives the handover: it is open while the message is being written,
 * busy for the one round trip that offers it to the daemon, and then either
 * closed -- with the outbox strip carrying on -- or back in front of the user
 * with an explanation. There is no state in which this window sits watching
 * something it is not responsible for any more.
 */
export type SendPhase = "idle" | "sending" | "failed";
export const sendPhase = signal<SendPhase>("idle");
export const sendNote = signal<string>("");

/** True once there is anything worth keeping. */
export const hasContent = computed(() => {
  const d = draft.value;
  return (
    d.to.length > 0 ||
    d.cc.length > 0 ||
    d.bcc.length > 0 ||
    d.subject.trim() !== "" ||
    d.body.trim() !== "" ||
    d.attachments.some((a) => !a.cid)
  );
});

/** Mutate the draft and start the autosave clock. */
export function patch(next: Partial<ComposerDraft>): void {
  batch(() => {
    draft.value = { ...draft.value, ...next };
    if (saveState.value !== "saving") saveState.value = "dirty";
  });
  mirror();
  scheduleSave();
}

// ---- opening ------------------------------------------------------------------

function defaultAccount(): string {
  return store.accounts.value[0]?.id ?? "";
}

function reset(next: Partial<ComposerDraft>, as: ComposeKind, id: string | null): void {
  batch(() => {
    draft.value = { ...EMPTY, accountId: defaultAccount(), ...next };
    kind.value = as;
    draftId.value = id;
    generation.value += 1;
    saveState.value = "clean";
    saveError.value = null;
    sendPhase.value = "idle";
    sendNote.value = "";
    store.composeOpen.value = true;
  });
  lastSaved = fingerprint(draft.value);
  void learnIdentities();
  void probeSubmission(draft.value.accountId);
  mirror();
}

/**
 * A blank message -- or whatever was left unfinished.
 *
 * Pressing Compose is also how someone comes back to a message a crashed tab
 * interrupted, so this looks for the mirror first. It is only ever non-empty
 * when a previous session ended between a keystroke and a save; a sent or
 * discarded draft clears it.
 */
export function openNew(to?: EmailAddress[]): void {
  if (!to && recoverMirror()) return;
  reset({ to: to ?? [], html: composeFormat.value === "rich" ? BLANK_HTML : null }, "new", null);
}

/** An empty rich body: one line to type on, above where a quote would go. */
const BLANK_HTML = '<div class="hm-written"><br></div>';

/**
 * A new message prefilled from a `mailto:` link the OS handed us.
 *
 * The counterpart to [`openNew`] for when HydraMail is the system's email app:
 * `navigation.mailtoFromLink` has already parsed the URL into recipients and
 * text, and this drops them into a fresh draft. The Cc/Bcc rows are shown only
 * when the link actually named one, the same rule a resumed draft follows -- a
 * populated Bcc must never hide. Unlike [`openNew`] it does not recover a
 * crashed draft first: a `mailto:` is an explicit "write to this person", not
 * "come back to what I was writing".
 */
export function openMailto(fields: {
  to: EmailAddress[];
  cc: EmailAddress[];
  bcc: EmailAddress[];
  subject: string;
  body: string;
}): void {
  reset(
    {
      to: fields.to,
      cc: fields.cc,
      bcc: fields.bcc,
      subject: fields.subject,
      body: fields.body,
      showCc: fields.cc.length > 0,
      showBcc: fields.bcc.length > 0,
    },
    "new",
    null,
  );
}

/**
 * Reply, or reply to everyone.
 *
 * The header work is the point. `In-Reply-To` and `References` come from the
 * original, because without them the reply starts a new conversation in every
 * client that threads -- and the user will not find out, since their own client
 * threads it by subject.
 *
 * Who it goes to:
 *
 *  - `Reply-To` when the original carries one, otherwise `From`. That header
 *    exists precisely to redirect replies, and ignoring it sends a list reply
 *    to the person who happened to post rather than to the list.
 *  - Reply-all adds the original `To` and `Cc`, then removes every address that
 *    is the user -- across *all* their accounts, not just the one this arrived
 *    on. Replying-all to yourself is the classic version of this bug and it is
 *    invisible to the person doing it.
 *
 * If that stripping empties the recipient list -- replying to a message you
 * sent to nobody but yourself -- the direct reply target goes back in, because
 * a reply with no recipients is not a defensible thing to hand back.
 *
 * Reply-all *waits* for the identities before doing that arithmetic. It did
 * not always, and the bug that shipped is worth recording: the identities were
 * learned as a side effect of opening the composer -- after the recipients
 * were already computed -- which happened to work when composers shared one
 * long-lived page, because some earlier composer had usually filled the set.
 * The moment each composer became a fresh window, every reply-all ran against
 * an empty set and stripped nobody, the user included. The wait is one JMAP
 * round trip on the first composer per window and free after that; addressing
 * mail to its own author on every reply-all was the worse deal.
 */
export async function openReply(row: Envelope, all = false): Promise<void> {
  if (all) await learnIdentities();
  const { email } = row;
  const direct = (email.replyTo?.length ? email.replyTo : email.from) ?? [];

  let to = dedupe(direct);
  let cc: EmailAddress[] = [];
  if (all) {
    to = withoutSelf(dedupe([...direct, ...(email.to ?? [])]));
    cc = withoutSelf(dedupe(email.cc ?? []));
    // Never leave both empty. Promote the first Cc, and failing that fall
    // back to the sender even though they are one of us. The promoted address
    // is not removed from Cc here -- the "To wins" filter below does that,
    // and an earlier version that tried to do it in place compared an array
    // against a fresh slice of itself, which is always false and never did
    // anything.
    if (to.length === 0) {
      to = cc.length > 0 ? cc.slice(0, 1) : dedupe(direct);
    }
    // An address cannot be in both rows; To wins.
    const inTo = new Set(to.map((a) => addressKey(a.email)));
    cc = cc.filter((a) => !inTo.has(addressKey(a.email)));
  }

  reset(
    {
      accountId: row.accountId,
      to,
      cc,
      showCc: cc.length > 0,
      subject: replySubject(email.subject),
      body: `\n\n${quote(email)}`,
      // In kind: an HTML original is quoted as HTML, a plain one as plain.
      // The text above is the alternative either way until the editor
      // reports its own.
      html: htmlOf(email) === null ? null : quoteHtml(email),
      attachments: inlineParts(email),
      ...threadHeaders(email),
    },
    all ? "replyAll" : "reply",
    null,
  );
}

/**
 * Forward, attachments and all.
 *
 * The attachments carry across by blob id, which works today and needs no
 * upload endpoint -- the daemon accepts both a whole-message blob and the
 * `<hash>.<partId>` spelling `Email/get` handed the client for each part.
 *
 * No `In-Reply-To` or `References`. A forward is a new conversation addressed
 * to someone who was not in the old one; threading it into the original would
 * file it under a discussion the recipient cannot see.
 */
export function openForward(row: Envelope): void {
  const { email } = row;
  reset(
    {
      accountId: row.accountId,
      // `fwd?` so Outlook's "FW:" counts as already-prefixed too; Thunderbird
      // recognises both spellings and stacking "Fwd: FW:" reads as a mistake.
      subject: forwardSubject(email.subject),
      body: `\n\n${forwardedBlock(email)}`,
      html: htmlOf(email) === null ? null : forwardHtml(email),
      attachments: [
        ...(email.attachments ?? [])
          .filter((p) => p.blobId !== null && p.disposition !== "inline")
          .map((p) => ({
            blobId: p.blobId!,
            name: p.name ?? "attachment",
            type: p.type || "application/octet-stream",
            size: p.size,
          })),
        // The pictures the HTML shows ride along by `cid`, so the forward
        // looks like the original rather than like a page of broken images.
        ...inlineParts(email),
      ],
    },
    "forward",
    null,
  );
}

/**
 * Resume a draft stored upstream.
 *
 * The point of storing drafts on the server rather than in `localStorage` is
 * that they can be reopened from the Drafts mailbox -- on another device, or
 * after this tab has been closed. Adopting `email.id` as {@link draftId} is
 * what makes continuing to edit *replace* that message rather than accumulate
 * beside it.
 */
export function openDraft(row: Envelope): void {
  const { email } = row;
  reset(
    {
      accountId: row.accountId,
      to: email.to ?? [],
      cc: email.cc ?? [],
      bcc: email.bcc ?? [],
      showCc: (email.cc?.length ?? 0) > 0,
      showBcc: (email.bcc?.length ?? 0) > 0,
      subject: email.subject ?? "",
      body: plainTextOf(email),
      // The format it was saved in. A draft with an HTML part reopens in the
      // editor; its inline pictures keep their ids so they are sent again.
      html: htmlOf(email),
      inReplyTo: email.inReplyTo,
      references: email.references,
      attachments: (email.attachments ?? [])
        .filter((p) => p.blobId !== null)
        .map((p) => ({
          blobId: p.blobId!,
          name: p.name ?? "attachment",
          type: p.type || "application/octet-stream",
          size: p.size,
          ...(p.cid && p.disposition === "inline" ? { cid: p.cid } : {}),
        })),
    },
    "draft",
    email.id,
  );
}

// ---- rich text ----------------------------------------------------------------

/** The original's HTML body, or `null` when it has none. */
export function htmlOf(email: Email): string | null {
  const part = email.htmlBody?.find((p) => p.type === "text/html");
  const id = part?.partId;
  return id ? (email.bodyValues?.[id]?.value ?? null) : null;
}

/**
 * The inline pictures the original's HTML refers to, as attachments of the
 * new message. Only the ones the markup names: a `cid` part nothing points at
 * is dead weight, and the reader draws the same distinction.
 */
function inlineParts(email: Email): ComposeAttachment[] {
  const html = htmlOf(email);
  if (html === null) return [];
  return (email.attachments ?? [])
    .filter((p) => p.cid !== null && p.blobId !== null && html.includes(`cid:${p.cid}`))
    .map((p) => ({
      blobId: p.blobId!,
      name: p.name ?? "image",
      type: p.type || "application/octet-stream",
      size: p.size,
      cid: p.cid,
    }));
}

/**
 * The reply's HTML: a line to write on, the attribution, and the original in
 * a `blockquote`. The rule on the quote is inline so it travels with the
 * message -- every other client's quote looks like this for the same reason.
 *
 * The original goes in whole, `<html>` and `<head>` included when it has
 * them: the editor's document parses it, drops what cannot nest, and hands
 * back the normalised result. That is also the only parse there is; nothing
 * here reads the markup.
 */
export function quoteHtml(email: Email): string {
  const who = email.from?.[0];
  const attribution = `On ${escapeHtml(whenSent(email))} ${who ? escapeHtml(formatAddress(who)) : "someone"} wrote:`;
  return (
    `${BLANK_HTML}<div class="hm-quote"><div class="hm-attr">${attribution}</div>` +
    `<blockquote type="cite" style="margin:0 0 0 .8ex;border-left:1px solid #ccc;padding-left:1ex">` +
    `${htmlOf(email) ?? ""}</blockquote></div>`
  );
}

/** The forward's HTML: the header block, then the original as it was. */
export function forwardHtml(email: Email): string {
  const line = (label: string, list: EmailAddress[] | null): string | null =>
    list && list.length > 0
      ? `${label}: ${escapeHtml(list.map(formatAddress).join(", "))}`
      : null;
  const headers = [
    line("From", email.from),
    `Date: ${escapeHtml(whenSent(email))}`,
    `Subject: ${escapeHtml(email.subject ?? "")}`,
    line("To", email.to),
    line("Cc", email.cc),
  ].filter((l): l is string => l !== null);
  return (
    `${BLANK_HTML}<div class="hm-quote"><div class="hm-attr">---------- Forwarded message ----------<br>` +
    `${headers.join("<br>")}</div><br>${htmlOf(email) ?? ""}</div>`
  );
}

/**
 * Plain text as HTML, for a message switched to rich text after it was begun:
 * one `div` per line, which is how browsers' editors spell a line anyway.
 */
export function textAsHtml(text: string): string {
  const lines = text.replace(/\r\n?/gu, "\n").split("\n");
  return lines.map((l) => `<div>${l === "" ? "<br>" : escapeHtml(l)}</div>`).join("");
}

/**
 * What the editor reported: the markup, and the plain alternative it derived.
 *
 * The first report after the editor loads is a `baseline`: the same message
 * said in the editor's own serialisation, not an edit. It is adopted without
 * dirtying the draft, so opening a reply does not file a draft nobody wrote --
 * unless the draft was already dirty, in which case the report is one more
 * change on top and is treated as one.
 */
export function fromEditor(content: { html: string; text: string }, baseline = false): void {
  if (baseline && saveState.value === "clean") {
    draft.value = { ...draft.value, html: content.html, body: content.text };
    lastSaved = fingerprint(draft.value);
    mirror();
    return;
  }
  patch({ html: content.html, body: content.text });
}

/**
 * Switch the message's format. To rich text keeps everything, as markup; to
 * plain keeps the words -- the editor's own plain rendering is already in
 * `body`, so nothing is converted here and nothing is lost that a plain
 * message could have held.
 */
export function setFormat(rich: boolean): void {
  const d = draft.value;
  if (rich && d.html === null) patch({ html: textAsHtml(d.body) || BLANK_HTML });
  if (!rich && d.html !== null) {
    patch({ html: null, attachments: d.attachments.filter((a) => !a.cid) });
  }
  generation.value += 1;
}

/**
 * The quoted original's pictures, as `data:` URIs for the editor to show.
 *
 * The same bytes the reader fetches for the same message; the editor swaps
 * them in for display and swaps `cid:` back on the way out. A picture that
 * cannot be fetched is left as a broken image in the editor and still sent by
 * id, which is the original's own state and not a loss.
 */
export async function inlineImages(d: ComposerDraft): Promise<Record<string, string>> {
  const session = store.session.value;
  const out: Record<string, string> = {};
  if (!session) return out;
  await Promise.all(
    d.attachments
      .filter((a) => a.cid)
      .map(async (a) => {
        try {
          const blob = await jmap.fetchAttachment(session, d.accountId, {
            blobId: a.blobId,
            name: a.name,
            type: a.type,
          });
          out[a.cid as string] = await new Promise<string>((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result as string);
            r.onerror = () => reject(r.error ?? new Error("unreadable"));
            r.readAsDataURL(blob);
          });
        } catch {
          /* shown broken, sent by id */
        }
      }),
  );
  return out;
}

function forwardSubject(subject: string | null): string {
  const base = (subject ?? "").trim();
  return /^fwd?:/iu.test(base) ? base : `Fwd: ${base}`;
}

function replySubject(subject: string | null): string {
  // Trimmed first: "Re:" hiding behind stray leading whitespace must not
  // gain a second one, which is exactly the stacking this test exists to
  // prevent. (Localized prefixes -- AW:, SV: -- are left alone on purpose;
  // Gmail does the same.)
  const base = (subject ?? "").trim();
  return /^re:/iu.test(base) ? base : `Re: ${base}`;
}

/**
 * `In-Reply-To` and `References` for a reply to `email`.
 *
 * `References` is the parent's own `References` followed by its `Message-ID`,
 * per RFC 5322 §3.6.4. When that list grows past what is reasonable the middle
 * is dropped rather than the head: the first id is what roots the thread, and
 * losing it is what splits a long discussion in two.
 */
function threadHeaders(email: Email): { inReplyTo: string[] | null; references: string[] | null } {
  const parent = email.messageId?.[0];
  if (!parent) return { inReplyTo: null, references: email.references ?? null };

  const chain = [...new Set([...(email.references ?? []), parent])];
  const trimmed = chain.length > 20 ? [chain[0]!, ...chain.slice(-19)] : chain;
  return { inReplyTo: [parent], references: trimmed };
}

/** The message as plain text, whatever the sender actually sent. */
function plainTextOf(email: Email): string {
  const value = (part: { partId: string | null } | undefined): string | null => {
    const id = part?.partId;
    return id ? (email.bodyValues?.[id]?.value ?? null) : null;
  };

  const text = email.textBody?.find((p) => p.type === "text/plain");
  const fromText = value(text);
  if (fromText !== null) return fromText;

  // A message with no plain part at all. Better a crude de-tagging than an
  // empty quote, which reads as though the original said nothing.
  const html = value(email.htmlBody?.[0]) ?? value(email.textBody?.[0]);
  return html === null ? "" : stripTags(html);
}

/**
 * A crude but honest de-tagging, for quoting a message that has no plain part.
 *
 * Exported for its tests only. Three things it now refuses to lose, each of
 * which is content and not decoration: a link's destination (the anchor text
 * alone tells the reader nothing they can follow), the quote depth an HTML
 * thread already carries in `<blockquote>` nesting (flattened, someone else's
 * words become indistinguishable from the sender's), and table cell
 * boundaries (run together, "£10 | £100" reads as "£10£100").
 *
 * Entity decoding order still matters and is pinned by a test: `&amp;` is
 * decoded last, so `&amp;lt;` becomes the literal text `&lt;` rather than
 * being decoded twice into `<`.
 */
export function stripTags(html: string): string {
  const codePoint = (n: number): string => {
    // Control characters are dropped rather than decoded: none belongs in a
    // quote, and letting one through would also let `&#0;` forge the NUL
    // blockquote markers below.
    if (n < 0x20 && n !== 0x0a && n !== 0x09) return "";
    try {
      return String.fromCodePoint(n);
    } catch {
      return "";
    }
  };

  const text = html
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/giu, "")
    // The destination rides along beside the anchor text -- unless the text
    // already IS the URL, where "(https://x) (https://x)" would be noise.
    // Only web and mail schemes: a stripped cid: or javascript: destination
    // is not something a reader can follow.
    .replace(
      /<a\b[^>]*\bhref\s*=\s*(?:"([^"]*)"|'([^']*)')[^>]*>([\s\S]*?)<\/a>/giu,
      (_m, dq: string | undefined, sq: string | undefined, inner: string) => {
        const url = (dq ?? sq ?? "").trim();
        if (!/^(https?:|mailto:)/iu.test(url)) return inner;
        return inner.trim() === url ? inner : `${inner} (${url})`;
      },
    )
    // Blockquote boundaries become markers on lines of their own; the pass
    // below turns everything between them into "> "-prefixed lines. NUL as
    // the marker because no entity decodes to it and no sane HTML carries it.
    .replace(/<blockquote\b[^>]*>/giu, "\n\u0000>\n")
    .replace(/<\/blockquote>/giu, "\n\u0000<\n")
    .replace(/<br\s*\/?>/giu, "\n")
    .replace(/<\/(td|th)>/giu, "\t")
    .replace(/<\/(p|div|tr|li|h[1-6])>/giu, "\n")
    .replace(/<[^>]+>/gu, "")
    .replace(/&nbsp;/gu, " ")
    .replace(/&lt;/gu, "<")
    .replace(/&gt;/gu, ">")
    .replace(/&quot;/gu, '"')
    .replace(/&#x([0-9a-f]+);/giu, (_, n: string) => codePoint(parseInt(n, 16)))
    .replace(/&#(\d+);/gu, (_, n: string) => codePoint(parseInt(n, 10)))
    .replace(/&amp;/gu, "&");

  const out: string[] = [];
  let depth = 0;
  for (const line of text.split("\n")) {
    if (line === "\u0000>") {
      depth += 1;
      continue;
    }
    if (line === "\u0000<") {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (depth === 0) {
      out.push(line);
      continue;
    }
    const prefix = "> ".repeat(depth);
    // No trailing space on an empty quoted line, same rule as quote().
    out.push(line === "" ? prefix.trimEnd() : prefix + line);
  }

  return (
    out
      .join("\n")
      .replace(/\n{3,}/gu, "\n\n")
      // The marker padding leaves quote-only lines at the edges ("> " with
      // nothing ever following); inside the text they are legitimate spacing.
      .replace(/^(?:[>\s]*\n)+/u, "")
      .replace(/(?:\n[>\s]*)+$/u, "")
      .trim()
  );
}

function whenSent(email: Email): string {
  const iso = email.sentAt ?? email.receivedAt;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // The shape every other client's attribution uses -- "Wed, Aug 5, 2026 at
  // 10:00 AM" -- rather than `toLocaleString()`, which drags seconds along
  // and reads like a log line. In the replier's zone with no zone marker,
  // which is what Gmail and Thunderbird also settle for.
  const day = new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
  const time = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
  return `${day} at ${time}`;
}

/** An attribution line and the original, `>` prefixed. */
function quote(email: Email): string {
  const who = email.from?.[0];
  // No comma before the name: "On <when> <who> wrote:" is the convention.
  const attribution = `On ${whenSent(email)} ${who ? formatAddress(who) : "someone"} wrote:`;
  const body = plainTextOf(email)
    .split(/\r?\n/u)
    // No trailing space on an empty quoted line: that is the "> " that mail
    // clients strip and diff tools complain about.
    .map((line) => (line === "" ? ">" : `> ${line}`))
    .join("\n");
  return `${attribution}\n${body}\n`;
}

/**
 * The forwarded-message block.
 *
 * Headers then the body verbatim, rather than `>` prefixed. That is what every
 * other client produces for a forward, and it is the right shape: a forward
 * presents the original *as* a message, where a reply quotes fragments of one
 * to answer them.
 */
function forwardedBlock(email: Email): string {
  const line = (label: string, list: EmailAddress[] | null): string | null =>
    list && list.length > 0 ? `${label}: ${list.map(formatAddress).join(", ")}` : null;

  const headers = [
    line("From", email.from),
    `Date: ${whenSent(email)}`,
    `Subject: ${email.subject ?? ""}`,
    line("To", email.to),
    line("Cc", email.cc),
  ].filter((l): l is string => l !== null);

  return `---------- Forwarded message ----------\n${headers.join("\n")}\n\n${plainTextOf(email)}\n`;
}

// ---- attachments --------------------------------------------------------------

/**
 * How many bytes of genuinely new content this daemon will accept. Zero today.
 *
 * `maxSizeUpload` is `0` because `/jmap/upload` is routed but not implemented:
 * the endpoint exists so a client is not handed a URL that 404s, and it answers
 * "not yet". Forwarding an existing attachment needs no upload -- the blob is
 * already on the server -- which is why forward works and attaching a file from
 * disk does not.
 *
 * Read from the session rather than hardcoded, so the day the endpoint lands
 * the button starts working without a change here.
 */
export const uploadLimit = computed(() => {
  const core = store.session.value?.capabilities["urn:ietf:params:jmap:core"] as
    | { maxSizeUpload?: number }
    | undefined;
  return core?.maxSizeUpload ?? 0;
});

export const canAttachNewFiles = computed(() => uploadLimit.value > 0);

/**
 * Put bytes on the server and get a blob id back.
 *
 * Written and unreachable, deliberately. Everything the upload path needs is
 * here -- the URL template from the session, the credential the browser
 * attaches itself, the error shape -- so wiring it up on the day the daemon
 * grows the endpoint is deleting a guard, not writing a feature. The guard is
 * in the caller, which checks {@link canAttachNewFiles} first, because the
 * honest failure is "this server cannot take new files" and not whatever a POST
 * to an unimplemented route happens to return.
 */
export async function uploadBlob(accountId: string, file: File): Promise<ComposeAttachment> {
  const template = store.session.value?.uploadUrl;
  if (!template) throw new Error("the session document carries no upload URL");

  const res = await fetch(template.replace("{accountId}", encodeURIComponent(accountId)), {
    method: "POST",
    credentials: "same-origin",
    // The file's own type, per RFC 8620 §6.1 -- and the bearer beside it. The
    // comment above this function said the browser attaches the credential
    // itself, which is true of a browser and of nothing else: a packaged build
    // has no cookie, so without this the upload is unauthenticated.
    headers: { "content-type": file.type || "application/octet-stream", ...bearer() },
    body: file,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      detail = (await res.json()).detail ?? detail;
    } catch {
      /* an HTML error page; the status line is the message */
    }
    throw new Error(detail);
  }
  const body = (await res.json()) as { blobId: string; size: number; type: string };
  return { blobId: body.blobId, name: file.name, type: body.type || file.type, size: body.size };
}

// ---- can this account send? -----------------------------------------------------

export type Submittable = "unknown" | "checking" | "yes" | "no";

/** Per account, because submission is configured per account. */
export const submittable = signal<Map<string, Submittable>>(new Map());
export const submissionProblem = signal<string | null>(null);

function setSubmittable(accountId: string, value: Submittable): void {
  const next = new Map(submittable.value);
  next.set(accountId, value);
  submittable.value = next;
}

export const canSendFrom = computed(
  () => submittable.value.get(draft.value.accountId) ?? "unknown",
);

/*
 * There was a `filesSentCopy(accountId)` here, reading a per-account capability
 * that said whether the submission server keeps its own record, so the composer
 * could destroy the local draft instead of filing it.
 *
 * It is gone, and the deletion is the point. The capability claims Gmail files
 * its own copy; a real send through that path produced no copy anywhere, on an
 * account whose sync was demonstrably current. A client that had believed it
 * would have deleted the only surviving record of a message that really was
 * sent. See {@link mutate.sendEmail} for the whole of that, and do not
 * reintroduce a destroy path keyed on a declaration -- key it on a copy that
 * has been observed to exist.
 */

/**
 * Ask whether this account has anywhere to send mail.
 *
 * The question is asked by making the *same* call the send will make, with an
 * empty `create`. `EmailSubmission/set` checks `can_submit` before it looks at
 * anything else and refuses the whole method with `forbidden`; with nothing to
 * create there is no row to enqueue and no message to build, so the probe
 * writes nothing whether it passes or fails.
 *
 * The alternative was to read it off the session document, which would be
 * cheaper and is what a client would normally do. It is not there: the session
 * advertises the submission capability for every account unconditionally, and
 * whether a *given* account has a submission host lives in its provider config,
 * which no client-facing endpoint exposes. Asking the method that enforces it
 * cannot drift from what sending will actually do, which is worth more than the
 * round trip.
 */
export async function probeSubmission(accountId: string): Promise<void> {
  if (!accountId) return;
  const known = submittable.value.get(accountId);
  // A "no" is deliberately *not* cached. It is the one answer somebody goes and
  // does something about -- on the accounts screen, in another tab, at a CLI --
  // and a client that remembered it would keep refusing to send for the life of
  // the page after the thing had been fixed. Asking again costs one method call
  // per time the composer is opened. "yes" is cached because nothing on this
  // screen turns sending off, and a stale one is corrected by the send itself.
  if (known === "yes" || known === "checking") return;

  const session = store.session.value;
  if (session && !Object.hasOwn(session.capabilities, SUBMISSION)) {
    // The daemon has no submission support at all. Asking would fail the whole
    // request with `unknownCapability`, which is a worse error than the true one.
    setSubmittable(accountId, "no");
    submissionProblem.value =
      "This daemon does not offer JMAP submission, so it cannot send mail at all.";
    return;
  }

  setSubmittable(accountId, "checking");
  try {
    const results = await call([["EmailSubmission/set", { accountId }, "probe"]], [SUBMISSION]);
    const args = results.get("probe");
    const error = args?.__error as { type?: string; description?: string } | undefined;
    if (!error) {
      setSubmittable(accountId, "yes");
      return;
    }
    setSubmittable(accountId, "no");
    submissionProblem.value =
      error.type === "forbidden"
        ? "This account has no sending set up. See its Sending section on the Accounts screen."
        : (error.description ?? "The server refused to accept a submission.");
  } catch (e) {
    // Unreachable is not the same as unconfigured. Leaving it unknown keeps the
    // Send button enabled, which is right: the send may well work, and a button
    // disabled by a failed probe is a feature removed by a network blip.
    setSubmittable(accountId, "unknown");
    submissionProblem.value = e instanceof Error ? e.message : String(e);
  }
}

/**
 * Forget what was learned about an account, because it has just been changed.
 *
 * Called by the accounts screen after a submission host is saved or cleared.
 * The probe already re-asks after a "no", so this exists for the other
 * direction: an account that could send and now cannot must stop looking
 * sendable, and nothing else in this client would notice.
 */
export function forgetSubmission(accountId: string): void {
  const next = new Map(submittable.value);
  next.delete(accountId);
  submittable.value = next;
  submissionProblem.value = null;
}

// ---- the crash guard ------------------------------------------------------------

// `MIRROR_KEY` is in `lib/keys.ts`, because signing out clears it and
// `lib/auth.ts` cannot import this file -- see that file for why.

/**
 * Where this window's crash guard is written.
 *
 * One key was right while there was one composer. There can now be several: a
 * compose window is a separate webview with its own JS context, and `localStorage`
 * is shared across every one of them because it is keyed by origin. So two
 * composers open at once wrote the same key on every keystroke, and each one's
 * recovery would have handed back the other's half-written message.
 *
 * The window's own address is what tells them apart, and it is available because
 * `lib/navigation.ts` pins it: a compose window's fragment is written back by
 * `wantedUrl` and does not move as the draft is typed, unlike the main window's,
 * which follows whichever message is open. That is the property this depends on
 * -- a key derived from a hash that changed would strand the guard under an
 * address nothing reads again.
 *
 * The main window keeps the bare key, which is not merely economy: it is what a
 * browser has always written, and changing it would abandon the unfinished
 * message of anyone who updates mid-sentence.
 *
 * `lib/auth.ts` sweeps every key with this prefix on sign-out. It has to: the
 * body of a message in plaintext under an origin the next person will also open
 * is the whole reason that sweep exists, and one that missed the compose windows
 * would leave exactly the mail somebody was in the middle of writing.
 */
function mirrorKey(): string {
  return windowRole() === "compose" ? `${MIRROR_KEY}:${location.hash}` : MIRROR_KEY;
}

/**
 * A copy in `localStorage`, and only that.
 *
 * Drafts live on the server; this is not a second store competing with them. It
 * covers one gap the server cannot: the seconds between a keystroke and the
 * next autosave, during which a closed tab or a crashed browser would take the
 * text with it. It carries {@link draftId} alongside the text, so restoring it
 * resumes the same upstream revision instead of forking a second one.
 *
 * # A public session does without it
 *
 * The crash guard is traded away on a shared machine, deliberately and with the
 * cost understood. This key is the body of a message in plaintext, written on a
 * keystroke, under an origin the next person at that machine will also open --
 * and it outlives the tab, the session and the cookie, because outliving them
 * is the entire point of it.
 *
 * So: a crash costs a paragraph, and somebody retypes it. The alternative costs
 * a stranger reading it, and nothing is retyped. Only one of those is
 * recoverable, and it is not the one that sounds worse in a changelog.
 */
function mirror(): void {
  if (isPublicSession()) return;
  try {
    if (!hasContent.value) {
      localStorage.removeItem(mirrorKey());
      return;
    }
    localStorage.setItem(
      mirrorKey(),
      JSON.stringify({ draft: draft.value, draftId: draftId.value, kind: kind.value }),
    );
  } catch {
    /* a full or disabled localStorage costs the crash guard, nothing else */
  }
}

function forgetMirror(): void {
  try {
    localStorage.removeItem(mirrorKey());
  } catch {
    /* as above */
  }
}

/**
 * Anything left behind by a tab that closed mid-sentence.
 *
 * The read side of the same gate, and it is the one worth having. A public
 * session that wrote nothing could still *recover* something -- and what it
 * would recover is the unfinished message of whoever used the machine before,
 * opened in a composer addressed from this person's account. Pressing Compose
 * on a library computer must not hand somebody a stranger's letter.
 */
export function recoverMirror(): boolean {
  if (isPublicSession()) return false;
  try {
    const raw = localStorage.getItem(mirrorKey());
    if (!raw) return false;
    const saved = JSON.parse(raw) as {
      draft: ComposerDraft;
      draftId: string | null;
      kind: ComposeKind;
    };
    batch(() => {
      draft.value = { ...EMPTY, ...saved.draft };
      draftId.value = saved.draftId;
      kind.value = saved.kind ?? "new";
      saveState.value = "dirty";
      store.composeOpen.value = true;
    });
    lastSaved = "";
    void learnIdentities();
    void probeSubmission(draft.value.accountId);
    return true;
  } catch {
    return false;
  }
}

// ---- autosave --------------------------------------------------------------------

/**
 * Long enough that typing a sentence is one save, short enough that stepping
 * away from the keyboard leaves the text upstream within a breath.
 *
 * Each save is an append on the provider -- a whole new message written and the
 * old one deleted -- so this is not a number to lower casually. Saving on every
 * keystroke would write a message per character.
 */
const SAVE_AFTER_MS = 3_000;

let saveTimer: ReturnType<typeof setTimeout> | undefined;
/**
 * Serialised, never concurrent.
 *
 * Two saves in flight at once is the other way to litter the Drafts folder: the
 * second one reads {@link draftId} before the first has written it, so both
 * pass the same `replacing` and the first one's revision is left behind.
 * Chaining onto the previous promise makes that ordering impossible.
 */
let inFlight: Promise<void> = Promise.resolve();
/**
 * Bumped by {@link discard}. A save already on its way when the user discards
 * lands afterwards with a new draft id that nothing will ever open; the save
 * sees the bump and destroys what it just wrote.
 */
let discards = 0;
let lastSaved = "";

function fingerprint(d: ComposerDraft): string {
  return JSON.stringify([d.accountId, d.to, d.cc, d.bcc, d.subject, d.body, d.html, d.attachments]);
}

function scheduleSave(): void {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => void saveNow(), SAVE_AFTER_MS);
}

/** The `Email` a save or a send creates. */
function toDraftInput(d: ComposerDraft, mailboxId: string, asDraft: boolean): DraftInput {
  const from = store.accounts.value.find((a) => a.id === d.accountId);
  const input: DraftInput = {
    mailboxIds: { [mailboxId]: true },
    ...(asDraft ? { keywords: { $draft: true } } : {}),
    ...(from?.name.includes("@") ? { from: [{ name: null, email: from.name }] } : {}),
    to: d.to,
    subject: d.subject,
    bodyValues: { body: { value: d.body } },
    textBody: [{ partId: "body", type: "text/plain" }],
  };
  if (d.html !== null) {
    // Both halves, text first in the structure the server builds; the text
    // is the editor's own rendering of the same markup, not a guess.
    input.bodyValues = { body: { value: d.body }, html: { value: d.html } };
    input.htmlBody = [{ partId: "html", type: "text/html" }];
  }
  if (d.cc.length > 0) input.cc = d.cc;
  if (d.bcc.length > 0) input.bcc = d.bcc;
  if (d.inReplyTo) input.inReplyTo = d.inReplyTo;
  if (d.references) input.references = d.references;
  // A picture the body no longer shows is not sent: the editor may have had
  // it deleted, or the message switched to plain text.
  const kept = d.attachments.filter((a) => !a.cid || (d.html ?? "").includes(`cid:${a.cid}`));
  if (kept.length > 0) {
    input.attachments = kept.map((a) => ({
      blobId: a.blobId,
      type: a.type,
      name: a.name,
      ...(a.cid ? { cid: a.cid, disposition: "inline" } : {}),
    }));
  }
  return input;
}

/**
 * The mailbox for a role, when the account has more than one claiming it.
 *
 * It happens, and Gmail is where: an account carries both a legacy top-level
 * `Sent` and the real `[Gmail]/Sent Mail`, and both are tagged `sent`. The same
 * is true of Drafts. Taking the first match picked the legacy folder -- two
 * messages in it against five hundred and thirty in the one Google actually
 * files into -- so sent mail was being filed where the user does not look, and
 * drafts staged in a folder the provider does not treat as its drafts folder.
 *
 * The tie-break is message count, and it is a proxy for the question that
 * cannot be asked directly: *which of these does the provider itself use?* No
 * client-facing field answers that, and the provider has been answering it in
 * the only way that leaves evidence -- by filing into one of them for years.
 * The empty duplicate is the vestigial one essentially by definition.
 *
 * Ties keep the first, which is the previous behaviour and the right answer
 * when there is genuinely nothing to choose between them -- a fresh account
 * where neither has been used yet, where either would do.
 */
function preferredMailbox(accountId: string, role: string): Mailbox | undefined {
  const matching = (store.mailboxes.value.get(accountId) ?? []).filter((m) => m.role === role);
  if (matching.length <= 1) return matching[0];
  return matching.reduce((best, m) => (m.totalEmails > best.totalEmails ? m : best));
}

function mailboxFor(accountId: string, role: string): string | undefined {
  return preferredMailbox(accountId, role)?.id;
}

/**
 * Where an outgoing message is built.
 *
 * `Email/set` has to name a mailbox — a message exists somewhere or it does not
 * exist — but for a send that mailbox is a staging area and not a destination.
 * `onSuccessUpdateEmail` moves the message to Sent the instant the submission
 * succeeds, so what is chosen here is where it spends a second or two, and only
 * where it stays if the send fails. Drafts is the right answer and the one
 * nearly every account gives.
 *
 * The fallback exists because "nearly" is not "always". An account can be
 * perfectly able to send -- credentials accepted, SMTP host reachable, a
 * recipient typed -- and simply have no Drafts folder, and refusing to send is
 * then a lost message over a missing folder. Saving a draft genuinely cannot
 * work without one, which is why {@link saveNow} still says so plainly; sending
 * can, so it does.
 *
 * The order is deliberate. Sent is second because a message that never leaves
 * the staging mailbox has, in the failure case, landed exactly where a sent
 * message belongs. Junk and Trash are never chosen at all -- both are folders
 * something else is entitled to empty, and a message waiting to go out is not
 * something to leave where a cleanup will take it. Inbox is last, because a
 * copy of your own outgoing mail arriving in your inbox reads as a bug even
 * when the send worked.
 */
function outgoingMailbox(accountId: string): Mailbox | undefined {
  for (const role of ["drafts", "sent", "archive", "inbox"]) {
    // Through the same tie-break as everything else, because an account with
    // two folders claiming `drafts` stages the message in whichever this picks
    // -- and on Gmail the legacy duplicate is not the one the provider treats
    // as its drafts folder.
    const hit = preferredMailbox(accountId, role);
    if (hit) return hit;
  }
  const boxes = store.mailboxes.value.get(accountId) ?? [];
  return boxes.find((m) => m.role !== "junk" && m.role !== "trash");
}

/**
 * Write the current text upstream now.
 *
 * Returns quietly when there is nothing to do, so callers may fire it at
 * blur, at close, and on the timer without checking first.
 */
export function saveNow(): Promise<void> {
  clearTimeout(saveTimer);
  inFlight = inFlight.then(async () => {
    const d = draft.value;
    if (!hasContent.value) return;
    const print = fingerprint(d);
    if (print === lastSaved) {
      // Nothing new -- but a "dirty" that turned out to match what is on the
      // server is saved, and the footer should say so rather than promise a
      // save that will never come.
      if (saveState.value === "dirty") saveState.value = draftId.value ? "saved" : "clean";
      return;
    }

    const drafts = mailboxFor(d.accountId, "drafts");
    if (!drafts) {
      // No Drafts folder to put it in. Said plainly rather than retried every
      // three seconds against a mailbox that is not going to appear.
      batch(() => {
        saveState.value = "failed";
        saveError.value =
          "This account has no Drafts folder, so the draft is kept only on this device. Sending " +
          "still works.";
      });
      return;
    }

    saveState.value = "saving";
    const epoch = discards;
    try {
      const { id } = await saveDraft(d.accountId, toDraftInput(d, drafts, true), draftId.value);
      if (discards !== epoch) {
        // Discarded while this was in the air: the draft it filed is one the
        // user threw away, and the composer's state is no longer this one's.
        await destroyEmails(d.accountId, [id]).catch(() => undefined);
        return;
      }
      lastSaved = print;
      batch(() => {
        draftId.value = id;
        saveState.value = fingerprint(draft.value) === print ? "saved" : "dirty";
        saveError.value = null;
      });
    } catch (e) {
      // The composer stays open and the text stays in it. A window closed over
      // a failed save is text the user cannot get back, and the server is the
      // one that failed, not them.
      batch(() => {
        saveState.value = "failed";
        saveError.value =
          e instanceof SetFailed || e instanceof Error
            ? `The draft was not saved: ${e.message}`
            : "The draft was not saved.";
      });
    }
  });
  return inFlight;
}

/**
 * Close the composer, saving first.
 *
 * Resolves to whether it is safe to close. A failed save keeps the window open
 * -- that is the whole contract of "never lose typed text", and it is the one
 * place where refusing the user's instruction is correct.
 */
export async function closeAfterSaving(): Promise<boolean> {
  if (!hasContent.value) {
    discardLocal();
    return true;
  }
  await saveNow();
  if (saveState.value === "failed") return false;
  batch(() => {
    store.composeOpen.value = false;
    sendPhase.value = "idle";
  });
  forgetMirror();
  leaveWindow();
  return true;
}

/** Put the composer away without touching what is stored upstream. */
function discardLocal(): void {
  batch(() => {
    draft.value = { ...EMPTY };
    draftId.value = null;
    saveState.value = "clean";
    saveError.value = null;
    sendPhase.value = "idle";
    sendNote.value = "";
    store.composeOpen.value = false;
  });
  lastSaved = "";
  forgetMirror();
  leaveWindow();
}

/**
 * Where a composer that is a whole window goes when it is put away.
 *
 * Clearing `composeOpen` is enough in the main window, where the composer is a
 * layer over the mail. In a window of its own there is nothing underneath, so
 * the same instruction has to reach the window manager or it leaves an empty
 * frame claiming there is still a message being written.
 *
 * **After the state has already been cleared, never instead of it.** Closing a
 * window is asynchronous and can fail -- and if it does, what is left on screen
 * must be a composer that has genuinely finished rather than one still holding
 * a draft it has stopped saving.
 */
function leaveWindow(): void {
  if (windowRole() !== "compose") return;
  void closeComposeWindow().catch((e: unknown) => {
    // The draft is saved and the window is still here, so the cost is a frame
    // somebody closes themselves. Said out loud because a Close button that
    // appears to do nothing reads as the app being broken -- the same rule
    // `ui/MessageWindow.tsx` follows for the merge that could not happen.
    store.toast(
      `The draft is saved, but this window would not close — ${
        e instanceof Error ? e.message : String(e)
      }`,
      "error",
    );
  });
}

/** Throw the draft away, upstream copy included. */
export async function discard(): Promise<void> {
  clearTimeout(saveTimer);
  discards += 1;
  const id = draftId.value;
  const accountId = draft.value.accountId;
  discardLocal();
  if (!id) return;
  try {
    await destroyEmails(accountId, [id]);
  } catch {
    store.toast("The draft was closed here, but the server still has a copy.", "error");
  }
}

// ---- sending -----------------------------------------------------------------------

/** Everything worth stopping to ask about before a message goes out. */
export interface Concern {
  key: string;
  text: string;
}

/**
 * The checks that are cheap and catch a real mistake.
 *
 * Warnings, not errors, except for having nobody to send to. Each one is
 * something a person does deliberately often enough that refusing would be
 * wrong -- an empty subject is fine between two people who just spoke, and the
 * attachment check is a heuristic over prose.
 */
export function concerns(): Concern[] {
  const d = draft.value;
  const out: Concern[] = [];

  if (d.to.length === 0 && d.cc.length === 0 && d.bcc.length === 0) {
    out.push({ key: "recipients", text: "There is nobody to send this to." });
  }
  if (d.subject.trim() === "") {
    out.push({ key: "subject", text: "The subject line is empty." });
  }
  // Only the text the user wrote, not the quoted original -- "please find
  // attached" three replies down is not a claim about this message.
  const written = d.body.split(/\n>|\n-{10} Forwarded/u)[0] ?? "";
  if (d.attachments.length === 0 && /\battach(ed|ing|ment)?\b/iu.test(written)) {
    out.push({ key: "attachment", text: "The message mentions an attachment but has none." });
  }
  return out;
}

export function canSend(): boolean {
  const d = draft.value;
  return (
    (d.to.length > 0 || d.cc.length > 0 || d.bcc.length > 0) &&
    canSendFrom.value !== "no" &&
    sendPhase.value !== "sending"
  );
}

/**
 * The undo window, as something the server will actually honour.
 *
 * The preference is a number of seconds and the wire wants an instant, so this
 * is where one becomes the other -- against {@link serverNow} rather than
 * `Date.now`, because a browser clock that is a few minutes out would otherwise
 * ask for a release time in the past and quietly buy no undo window at all.
 *
 * Whole seconds, rounded up, matching the granularity the daemon stores and
 * renders. A window asked for as 10 and honoured as 9.4 is not worth the
 * fractional digits it would cost to express.
 */
function releaseFor(seconds: number | null): Release {
  // Nobody has chosen, so the deployment's own hold applies. This is the
  // default and it is the honest one: the operator's number is the only number,
  // and no client is second-guessing it.
  if (seconds === null) return "server";
  // Off. Not a `sendAt` of now -- RFC 8621 has a way to say "I do not want an
  // undo window", and saying it plainly is what stops a zero-length window
  // being drawn as a countdown that flickers past.
  if (seconds <= 0) return "immediate";
  const at = new Date(Math.ceil((serverNow() + seconds * 1000) / 1000) * 1000);
  return { sendAt: at.toISOString().replace(/\.\d+Z$/u, "Z") };
}

/**
 * Offer the message to the daemon, put it on the outbox strip, and get out of
 * the way.
 *
 * `sendEmail` resolving means the daemon has written an outbox row -- queued,
 * held, or already on its way, but not sent. What happens after that is
 * `lib/outbox.ts`'s job, and the handover is the only delicate part: the strip
 * has to be showing the message *before* this window closes, or a failure
 * afterwards would have nowhere to appear.
 *
 * A refusal leaves the composer open with every word still in it, which is
 * unchanged and non-negotiable.
 */
export async function send(): Promise<void> {
  const d = draft.value;
  clearTimeout(saveTimer);

  const staging = outgoingMailbox(d.accountId);
  const sent = mailboxFor(d.accountId, "sent");
  if (!staging) {
    batch(() => {
      sendPhase.value = "failed";
      sendNote.value =
        "This account has no mailboxes yet, so there is nowhere to build the message. " +
        "It is still syncing — try again in a moment.";
    });
    return;
  }

  // Where a message that does not get away ends up, said in the words the user
  // will see in their own folder list. Every failure path below quotes this,
  // because "it is saved in Drafts" is a lie on an account with no Drafts
  // folder, and being told to look somewhere that does not exist is worse than
  // being told nothing.
  const keptIn =
    staging.role === "drafts"
      ? "it is saved in Drafts"
      : `it is kept in this browser and a copy is in ${staging.name}`;

  batch(() => {
    sendPhase.value = "sending";
    sendNote.value = "Handing the message to the server…";
  });

  let result;
  try {
    result = await sendEmail(d.accountId, toDraftInput(d, staging.id, true), {
      // RFC 8621 requires an identity id and this daemon has no `Identity`
      // objects to choose between -- an account has exactly one sending
      // identity, the one its submission config describes. The account id is
      // the stable, meaningful thing to send, and it is what an `Identity/get`
      // would key off when per-account aliases arrive.
      identityId: d.accountId,
      ...(sent ? { sentMailboxId: sent } : {}),
      replacing: draftId.value,
      release: releaseFor(undoSendSeconds.value),
    });
    // The draft was consumed by the send: the id it had is gone, and holding it
    // would make the next save try to destroy a message that no longer exists.
    draftId.value = null;
  } catch (e) {
    batch(() => {
      sendPhase.value = "failed";
      sendNote.value =
        e instanceof SetFailed || e instanceof Error
          ? `${e.message} Your message is still here, and ${keptIn}.`
          : `The message was not sent. Your message is still here, and ${keptIn}.`;
    });
    // Whatever the send did or did not create, the text is still on screen.
    void saveNow();
    return;
  }

  // The handover, and the line this window is allowed to close after. Every
  // field comes from what was just sent rather than from a read-back, so there
  // is no round trip during which the message exists nowhere the user can see.
  // Announced through the transport rather than registered on this window's
  // own strip, because on the desktop this window is about to stop existing —
  // the strip that must show the undo button is the main window's, and a
  // local call drew it on the one surface guaranteed to disappear. Awaited:
  // the promise resolving means the shell (or, in a browser, the strip
  // itself) has the message, so closing cannot race the delivery.
  const sendAt = result.sendAt ? Date.parse(result.sendAt) : NaN;
  await announceSubmission({
    accountId: d.accountId,
    submissionId: result.submissionId,
    emailId: result.emailId,
    subject: d.subject,
    recipients: [...d.to, ...d.cc, ...d.bcc].map((a) => a.email),
    sendAt: Number.isNaN(sendAt) ? null : sendAt,
  });

  // Where the copy will land, said once, and only when it is not where anyone
  // would look. An account with a Sent folder needs no explanation; one without
  // is about to file a sent message somewhere surprising, and silence there is
  // how mail goes missing. It is said now rather than on delivery because this
  // window is about to stop existing.
  if (!sent) {
    store.toast(`This account has no Sent mailbox, so the copy will be in ${staging.name}.`);
  }

  forgetMirror();
  discardLocal();
  void store.refresh({ quiet: true });
}

/** Put the composer back into an editable state after a failed send. */
export function backToEditing(): void {
  batch(() => {
    sendPhase.value = "idle";
    sendNote.value = "";
  });
}
